import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../Layout/LanguageContext';

const GA_MEASUREMENT_ID = 'AW-18205709651';
const CONSENT_STORAGE_KEY = 'cookie-consent-v1';

type ConsentValue = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let googleTagLoaded = false;

const loadGoogleTag = () => {
  if (googleTagLoaded || typeof window.gtag !== 'function') return;
  googleTagLoaded = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
};

const updateConsent = (value: ConsentValue) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

const readStoredConsent = (): ConsentValue | null => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored === 'granted' || stored === 'denied' ? stored : null;
  } catch {
    return null;
  }
};

const storeConsent = (value: ConsentValue) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    /* localStorage nicht verfuegbar - Einwilligung gilt dann nur fuer die Sitzung */
  }
};

interface CookieConsentContextValue {
  openSettings: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue>({
  openSettings: () => {},
});

export const useCookieConsent = () => useContext(CookieConsentContext);

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99999;
  background: var(--background-light);
  border-top: 4px solid var(--accent-color);
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.18);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    padding: 1.25rem;
  }
`;

const TextBlock = styled.div`
  flex: 1;
  color: var(--text-color);
  font-size: 0.95rem;
  line-height: 1.55;

  strong {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 1.05rem;
    color: var(--primary-color);
  }

  a {
    color: var(--primary-color);
    text-decoration: underline;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 900px) {
    flex-direction: column-reverse;
  }
`;

const BaseButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.85rem 1.6rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.92;
  }
`;

const DeclineButton = styled(BaseButton)`
  background: var(--secondary-color);
  color: #ffffff;
`;

const AcceptButton = styled(BaseButton)`
  background: var(--accent-color);
  color: var(--primary-dark);
`;

const content = {
  de: {
    title: 'Datenschutz-Einstellungen',
    body: 'Wir verwenden notwendige Cookies, damit diese Website funktioniert. Mit Ihrer Einwilligung nutzen wir zusätzlich Google Ads, um die Wirksamkeit unserer Werbung zu messen (Conversion-Tracking). Ihre Einwilligung ist freiwillig und jederzeit widerrufbar.',
    more: 'Mehr in der Datenschutzerklärung',
    decline: 'Nur notwendige',
    accept: 'Alle akzeptieren',
  },
  en: {
    title: 'Privacy settings',
    body: 'We use necessary cookies to make this website work. With your consent, we additionally use Google Ads to measure the effectiveness of our advertising (conversion tracking). Your consent is voluntary and can be withdrawn at any time.',
    more: 'More in the privacy policy',
    decline: 'Necessary only',
    accept: 'Accept all',
  },
};

const CookieBanner: React.FC<{
  onAccept: () => void;
  onDecline: () => void;
}> = ({ onAccept, onDecline }) => {
  const { language } = useLanguage();
  const t = content[language];
  const privacyHref = language === 'de' ? '/privacy-de' : '/privacy-en';

  return (
    <Banner role="dialog" aria-live="polite" aria-label={t.title}>
      <TextBlock>
        <strong>{t.title}</strong>
        {t.body}{' '}
        <Link to={privacyHref}>{t.more}</Link>
      </TextBlock>
      <Buttons>
        <DeclineButton type="button" onClick={onDecline}>
          {t.decline}
        </DeclineButton>
        <AcceptButton type="button" onClick={onAccept}>
          {t.accept}
        </AcceptButton>
      </Buttons>
    </Banner>
  );
};

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored === 'granted') {
      updateConsent('granted');
      loadGoogleTag();
    } else if (stored === null) {
      setVisible(true);
    }
    // 'denied' => Standard bleibt abgelehnt, kein Tag wird geladen
  }, []);

  const accept = useCallback(() => {
    storeConsent('granted');
    updateConsent('granted');
    loadGoogleTag();
    setVisible(false);
  }, []);

  const decline = useCallback(() => {
    storeConsent('denied');
    updateConsent('denied');
    setVisible(false);
  }, []);

  const openSettings = useCallback(() => setVisible(true), []);

  return (
    <CookieConsentContext.Provider value={{ openSettings }}>
      {children}
      {visible && <CookieBanner onAccept={accept} onDecline={decline} />}
    </CookieConsentContext.Provider>
  );
};
